import { characters, getCharacterBySlug, getCharacterField } from "./characters";

const LANG_PREFIXES = ["/zh", "/ja"];

function stripLangPrefix(pathname: string): string {
  for (const p of LANG_PREFIXES) {
    if (pathname === p) return "/";
    if (pathname.startsWith(p + "/")) return pathname.slice(p.length);
  }
  return pathname || "/";
}

function buildLangUrl(lang: string): string {
  const prefix = lang === "en" ? "" : `/${lang}`;
  const rest = stripLangPrefix(window.location.pathname);
  return `${prefix}${rest}${window.location.search}${window.location.hash}`;
}

// Map common name variants (en/zh/ja, with or without species) → slug
const NAME_TO_SLUG: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, "");
  for (const c of characters) {
    [c.name, c.fullName, c.nameZh, c.fullNameZh, c.nameJa, c.fullNameJa, c.slug].forEach((n) => {
      if (n) map[norm(n)] = c.slug;
    });
  }
  return map;
})();

function resolveCharacterSlug(name: string): string | null {
  const key = name.toLowerCase().replace(/\s+/g, "");
  if (NAME_TO_SLUG[key]) return NAME_TO_SLUG[key];
  // partial match fallback
  for (const [k, slug] of Object.entries(NAME_TO_SLUG)) {
    if (k.includes(key) || key.includes(k)) return slug;
  }
  return null;
}

export function registerWebMCP(): void {
  if (typeof navigator === "undefined" || !("modelContext" in navigator)) return;
  const ctx = navigator.modelContext;
  if (!ctx?.provideContext) return;

  try {
    ctx.provideContext({
      tools: [
        {
          name: "navigate_to_characters",
          description: "Navigate to the Queebi Land characters page.",
          inputSchema: { type: "object", properties: {} },
          execute: () => {
            window.location.assign("/queebi-land-characters");
            return { ok: true };
          },
        },
        {
          name: "navigate_to_audiobook",
          description: "Navigate to the Queebi Land audiobook page.",
          inputSchema: { type: "object", properties: {} },
          execute: () => {
            window.location.assign("/audiobook");
            return { ok: true };
          },
        },
        {
          name: "navigate_to_products",
          description: "Navigate to the Queebi products page.",
          inputSchema: { type: "object", properties: {} },
          execute: () => {
            window.location.assign("/queebi-products");
            return { ok: true };
          },
        },
        {
          name: "play_audiobook_normal",
          description: "Open the audiobook page and scroll to the normal-speed player.",
          inputSchema: { type: "object", properties: {} },
          execute: () => {
            window.location.assign("/audiobook#normal");
            return { ok: true };
          },
        },
        {
          name: "play_audiobook_slow",
          description: "Open the audiobook page and scroll to the slow-speed player.",
          inputSchema: { type: "object", properties: {} },
          execute: () => {
            window.location.assign("/audiobook#slow");
            return { ok: true };
          },
        },
        {
          name: "switch_language",
          description: "Switch the site language. Accepts 'en', 'zh' (Traditional Chinese), or 'ja' (Japanese).",
          inputSchema: {
            type: "object",
            properties: { lang: { type: "string", enum: ["en", "zh", "ja"] } },
            required: ["lang"],
          },
          execute: (args) => {
            const lang = String((args as { lang?: string }).lang || "en");
            if (!["en", "zh", "ja"].includes(lang)) return { ok: false, error: "Invalid lang" };
            window.location.assign(buildLangUrl(lang));
            return { ok: true, lang };
          },
        },
        {
          name: "get_character_bio",
          description:
            "Return the bio, personality, interests, and favorite things for a Queebi Land character by name (English, Chinese, or Japanese).",
          inputSchema: {
            type: "object",
            properties: {
              name: { type: "string", description: "Character name, e.g. 'Liam', '力力獅', 'Fabio'." },
              lang: { type: "string", enum: ["en", "zh", "ja"], description: "Language for the response (default: en)." },
            },
            required: ["name"],
          },
          execute: (args) => {
            const { name, lang } = args as { name?: string; lang?: string };
            if (!name) return { ok: false, error: "Missing name" };
            const slug = resolveCharacterSlug(name);
            if (!slug) return { ok: false, error: `Unknown character: ${name}` };
            const c = getCharacterBySlug(slug);
            if (!c) return { ok: false, error: `Not found: ${slug}` };
            const l = lang || "en";
            return {
              ok: true,
              slug: c.slug,
              fullName: getCharacterField(c, "fullName", l),
              bio: getCharacterField(c, "bio", l),
              personality: getCharacterField(c, "personality", l),
              interests: getCharacterField(c, "interests", l),
              favoriteThings: getCharacterField(c, "favoriteThings", l),
              url: `https://queebi.com/${c.slug}`,
            };
          },
        },
      ],
    });
  } catch (err) {
    // Silently ignore — WebMCP is experimental and may throw on unsupported shapes.
    console.debug("WebMCP registration failed", err);
  }
}
